// pages/components/settings/Security.jsx
import React, { useState } from 'react';
import { Lock, Eye, EyeOff, Check, AlertCircle } from 'lucide-react';
import apiRequest from '@/services/requests';

const Security = () => {
const [showCurrentPassword, setShowCurrentPassword] = useState(false);
const [showNewPassword, setShowNewPassword] = useState(false);
const [showConfirmPassword, setShowConfirmPassword] = useState(false);
const [loading, setLoading] = useState(false);
const [message, setMessage] = useState({ type: '', text: '' });

const [formData, setFormData] = useState({
currentPassword: '',
newPassword: '',
confirmPassword: ''
});

const passwordRequirements = [
{ label: 'At least 8 characters', test: (pwd) => pwd.length >= 8 },
{ label: 'Contains uppercase letter', test: (pwd) => /[A-Z]/.test(pwd) },
{ label: 'Contains lowercase letter', test: (pwd) => /[a-z]/.test(pwd) },
{ label: 'Contains number', test: (pwd) => /\d/.test(pwd) },
];

const handleInputChange = (e) => {
const { name, value } = e.target;
setFormData(prev => ({ ...prev, [name]: value }));
setMessage({ type: '', text: '' });
};

const handleSubmit = async (e) => {
e.preventDefault();

if (formData.newPassword !== formData.confirmPassword) {
setMessage({ type: 'error', text: 'Passwords do not match' });
return;
}

if (formData.newPassword.length < 8) {
setMessage({ type: 'error', text: 'Password must be at least 8 characters long' });
return;
}

setLoading(true);

try {
const res = await apiRequest.changePassword(
formData.currentPassword,
formData.newPassword
);

if (res.success) {
setMessage({ type: 'success', text: 'Password changed successfully!' });
setFormData({
currentPassword: '',
newPassword: '',
confirmPassword: ''
});
}
} catch (error) {
setMessage({ 
type: 'error', 
text: error.response?.data?.message || 'Failed to change password' 
});
}

setLoading(false);
};

return (
<div className="space-y-8">
<div>
<h2 className="hidden text-2xl font-bold text-slate-900 mb-2">Security</h2>
<p className="hidden text-slate-600">Manage your password and security settings</p>
</div>

<form onSubmit={handleSubmit} className="space-y-6 max-w-xl">
{/* Current Password */}
<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Current Password *
</label>
<div className="relative">
<Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
<input
type={showCurrentPassword ? 'text' : 'password'}
name="currentPassword"
value={formData.currentPassword}
onChange={handleInputChange}
required
className="w-full pl-11 pr-11 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
/>
<button
type="button"
onClick={() => setShowCurrentPassword(!showCurrentPassword)}
className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
>
{showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
</button>
</div>
</div>

{/* New Password */}
<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
New Password *
</label>
<div className="relative">
<Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
<input
type={showNewPassword ? 'text' : 'password'}
name="newPassword"
value={formData.newPassword}
onChange={handleInputChange}
required
className="w-full pl-11 pr-11 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
/>
<button
type="button"
onClick={() => setShowNewPassword(!showNewPassword)}
className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
>
{showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
</button>
</div>

{/* Password Requirements */}
{formData.newPassword && (
<div className="mt-3 space-y-2">
{passwordRequirements.map((req, idx) => {
const passed = req.test(formData.newPassword);
return (
<div key={idx} className="flex items-center gap-2 text-sm">
  <div className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
    passed ? 'bg-green-100' : 'bg-slate-100'
  }`}>
    {passed && <Check size={14} className="text-green-600" />}
  </div>
  <span className={passed ? 'text-green-700' : 'text-slate-500'}>
    {req.label}
  </span>
</div>
);
})}
</div>
)}
</div>

{/* Confirm Password */}
<div>
<label className="block text-sm font-semibold text-slate-700 mb-2">
Confirm New Password *
</label>
<div className="relative">
<Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
<input
type={showConfirmPassword ? 'text' : 'password'}
name="confirmPassword"
value={formData.confirmPassword}
onChange={handleInputChange}
required
className="w-full pl-11 pr-11 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
/>
<button
type="button"
onClick={() => setShowConfirmPassword(!showConfirmPassword)}
className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
>
{showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
</button>
</div>

{formData.confirmPassword && formData.newPassword !== formData.confirmPassword && (
<p className="mt-2 text-sm text-red-600 flex items-center gap-1">
<AlertCircle size={16} />
Passwords do not match
</p>
)}
</div>

{/* Message */}
{message.text && (
<div className={`p-4 rounded-lg ${
message.type === 'success' 
? 'bg-green-50 text-green-800 border border-green-200' 
: 'bg-red-50 text-red-800 border border-red-200'
}`}>
<div className="flex items-center gap-2">
{message.type === 'success' ? (
<Check size={20} />
) : (
<AlertCircle size={20} />
)}
<p className="font-medium">{message.text}</p>
</div>
</div>
)}

{/* Submit Button */}
<button
type="submit"
disabled={loading}
className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white font-semibold rounded-lg shadow-md transition-all"
>
{loading ? 'Changing Password...' : 'Change Password'}
</button>
</form>

{/* Security Tips */}
<div className="mt-8 p-6 bg-blue-50 border border-blue-200 rounded-lg">
<h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
<Lock size={20} />
Security Tips
</h3>
<ul className="space-y-2 text-sm text-blue-800">
<li className="flex items-start gap-2">
<span className="text-blue-600 mt-0.5">•</span>
Use a unique password that you don't use for other websites
</li>
<li className="flex items-start gap-2">
<span className="text-blue-600 mt-0.5">•</span>
Consider using a password manager to generate and store complex passwords
</li>
<li className="flex items-start gap-2">
<span className="text-blue-600 mt-0.5">•</span>
Change your password regularly (every 3-6 months)
</li>
<li className="flex items-start gap-2">
<span className="text-blue-600 mt-0.5">•</span>
Never share your password with anyone
</li>
</ul>
</div>
</div>
);
};

export default Security;